import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { HEROES, HERO_ORDER } from "../data/heroes";

const HeroCard = ({ heroKey, onRead, onEnter }) => {
  const { t } = useTranslation();
  const hero = HEROES[heroKey];
  const tr = t(`heroes.${heroKey}`, { returnObjects: true });

  return (
    <Card
      data-testid={`hero-card-${heroKey}`}
      className={`relative overflow-hidden border-0 bg-gradient-to-br ${hero.soft} ${hero.bgGlow} group transition-all duration-500 hover:-translate-y-2`}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${hero.gradient}`} />
      <div className={`aspect-[3/4] w-full overflow-hidden ${hero.imageBg || 'bg-slate-900'} flex items-end justify-center`}>
        <img
          src={hero.image}
          alt={tr.name}
          className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-105 drop-shadow-2xl"
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-xl font-extrabold tracking-tight text-gray-900">{tr.name}</h3>
            <p className="text-xs uppercase tracking-widest text-gray-500 mt-0.5">{tr.subname}</p>
          </div>
          <Badge className={`${hero.chip} border-0`}>{tr.pathTitle}</Badge>
        </div>
        <p className="text-sm font-semibold text-gray-800 mb-3 italic">"{tr.tagline}"</p>
        <p className="text-xs text-gray-600 tracking-wide mb-4">{tr.mantra}</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRead(heroKey)}
            data-testid={`hero-read-${heroKey}`}
            className="flex-1"
          >
            {t("heroes.readStory")}
          </Button>
          <Button
            size="sm"
            onClick={() => onEnter(heroKey)}
            data-testid={`hero-enter-${heroKey}`}
            className={`flex-1 bg-gradient-to-r ${hero.gradient} text-white border-0 hover:opacity-90`}
          >
            {t("heroes.enterPath")}
          </Button>
        </div>
      </div>
    </Card>
  );
};

const HeroSection = ({ variant = "landing" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openHero, setOpenHero] = useState(null);

  const handleEnter = (heroKey) => {
    if (variant === "landing") navigate("/auth");
    else navigate(`/activities?hero=${heroKey}`);
  };

  const trOpen = openHero ? t(`heroes.${openHero}`, { returnObjects: true }) : null;
  const heroOpen = openHero ? HEROES[openHero] : null;

  return (
    <div className="py-12" data-testid="hero-section">
      <div className="text-center mb-10">
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-3">
          {t("heroes.chooseYourHero")}
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t("heroes.chooseSubtitle")}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto px-4">
        {HERO_ORDER.map((k) => (
          <HeroCard key={k} heroKey={k} onRead={setOpenHero} onEnter={handleEnter} />
        ))}
      </div>

      {/* Story Dialog */}
      <Dialog open={!!openHero} onOpenChange={(v) => !v && setOpenHero(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0" data-testid="hero-story-dialog">
          {heroOpen && trOpen && (
            <>
              <div className={`h-2 bg-gradient-to-r ${heroOpen.gradient}`} />
              <div className="grid md:grid-cols-2">
                <div className={`flex items-end justify-center ${heroOpen.imageBg || 'bg-slate-900'} p-4`}>
                  <img
                    src={heroOpen.image}
                    alt={trOpen.name}
                    className="max-w-full max-h-96 object-contain drop-shadow-2xl"
                  />
                </div>
                <div className="p-6">
                  <DialogHeader className="mb-3">
                    <DialogTitle className="text-2xl font-extrabold">{trOpen.name}</DialogTitle>
                    <DialogDescription className="text-xs uppercase tracking-widest">
                      {trOpen.subname}
                    </DialogDescription>
                  </DialogHeader>
                  <p className="italic font-semibold text-gray-800 mb-2">"{trOpen.tagline}"</p>
                  <p className="text-xs text-gray-500 mb-4">{trOpen.mantra}</p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-5">{trOpen.story}</p>
                  <Button
                    onClick={() => {
                      setOpenHero(null);
                      handleEnter(heroOpen.key);
                    }}
                    className={`w-full bg-gradient-to-r ${heroOpen.gradient} text-white border-0 hover:opacity-90`}
                    data-testid={`hero-dialog-enter-${heroOpen.key}`}
                  >
                    {t("heroes.enterPath")} →
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeroSection;
